<template>
  <div>
    <div class="bg-white dark-bg-custom h-[70vh] w-full flex flex-col">
      <!-- Header: Tab Navigation (fixed) -->
      <div class="p-2 border-b border-gray-200">
        <div class="flex justify-between">
          <div>
            <button
              v-for="tab in tabs"
              :key="tab.key"
              @click="setActiveTab(tab.key)"
              :class="[
                'px-5 py-2 cursor-pointer border-b-3 font-medium transition-all duration-200',
                activeTab === tab.key
                  ? 'text-blue-600 border-blue-500'
                  : 'text-gray-600 border-transparent hover:text-blue-600'
              ]"
            >
              {{ tab.label }}
            </button>
          </div>
          <v-btn icon @click="cancel" v-if="enableSave">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Body: Scrollable content -->
      <div class="flex-1 p-5 overflow-y-auto">
        <!-- Upload Tab -->
        <div v-if="activeTab === 'upload'" class="tab-content">
          <div
            @drop="handleDrop"
            @dragover="handleDragOver"
            @dragenter="handleDragEnter"
            @dragleave="handleDragLeave"
            :class="[
              'border-2 border-dashed rounded-lg p-8 text-center  mb-5 transition-all duration-200 cursor-pointer',
              isDragOver ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-blue-500',
              selectedFile ? '' : 'h-[30vh]'
            ]"
            @click="$refs.fileInput.click()"
          >
            <div v-if="!selectedFile" class="tab-items items-center h-full">
              <div>
                <div class="text-4xl mb-3">📁</div>
                <h3 class="text-lg font-semibold mb-2">Drag & Drop your Excel file here</h3>
                <p class="text-gray-600 mb-3">or</p>
                <button
                  class="px-5 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50 transition-colors"
                >
                  Browse Files
                </button>
                <input
                  ref="fileInput"
                  type="file"
                  accept=".xlsx,.xls"
                  @change="handleFileSelect"
                  class="hidden"
                />
              </div>
            </div>
            <!-- Selected File Display -->
            <div v-else class="flex items-center bg-gray-100 p-3 rounded-lg w-full h-8">
              <span class="flex-1 text-left truncate">{{ selectedFile.name }}</span>
              <button @click.stop="removeFile" class="text-red-500 text-xl ml-3">&times;</button>
            </div>
          </div>

          <!-- Validation Results -->
          <div v-if="validationMessages.length > 0" class="mb-5">
            <div
              v-for="(message, index) in validationMessages"
              :key="index"
              :class="[
                'p-3 mb-2 border-l-4',
                message.type === 'error'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-green-500 bg-green-50 text-green-700'
              ]"
            >
              {{ message.text }}
            </div>
          </div>

          <!-- Upload Progress -->
          <div v-if="uploading" class="mt-5">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="bg-blue-500 h-2 rounded-full transition-all duration-300"
                :style="{ width: uploadProgress + '%' }"
              ></div>
            </div>
            <p class="text-center mt-2 text-sm text-gray-600">{{ Math.ceil(uploadProgress) }}%</p>
          </div>

          <!-- Upload Results -->
          <div v-if="uploadComplete" class="mt-5">
            <!-- Success Summary -->
            <div
              v-if="uploadResults.success > 0"
              class="border border-green-200 rounded-lg p-4 mb-4"
            >
              <div class="flex items-center">
                <div class="text-green-500 text-2xl mr-4">✓</div>
                <div>
                  <h4 class="text-lg font-semibold text-green-700">
                    {{ uploadResults.success }} {{ entityName }}s Created Successfully
                  </h4>
                  <p class="text-sm text-gray-600">
                    These {{ entityName }}s have been added to your system.
                  </p>
                </div>
              </div>
            </div>

            <!-- Failed Summary -->
            <div
              v-if="uploadResults.failed > 0 && selectedFile"
              class="border border-red-200 rounded-lg p-4 mb-4"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <div class="text-red-500 text-2xl mr-4">⚠</div>
                  <div>
                    <h4 class="text-lg font-semibold text-red-700">
                      {{ uploadResults.failed }} {{ entityName }}s Failed
                    </h4>
                    <p class="text-sm text-gray-600">
                      These {{ entityName }}s could not be processed due to errors.
                    </p>
                  </div>
                </div>
                <button
                  @click="downloadFailedItems"
                  :disabled="downloadingFailed"
                  class="px-4 py-2 border border-red-500 text-red-500 rounded hover:bg-red-50 transition-colors"
                >
                  <span v-if="downloadingFailed">Downloading...</span>
                  <span v-else>Download Failed</span>
                </button>
              </div>
            </div>

            <!-- Failed Items Details -->
            <div v-if="invalidItems.length > 0" class="border border-gray-200 rounded-lg">
              <button
                @click="showFailedDetails = !showFailedDetails"
                class="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div class="flex items-center">
                  <span class="text-red-500 mr-2">⚠</span>
                  <span class="font-medium"
                    >View Failed {{ entityName }}s Details ({{ invalidItems.length }})</span
                  >
                </div>
                <span class="text-gray-400">{{ showFailedDetails ? '−' : '+' }}</span>
              </button>
              <div
                v-if="showFailedDetails"
                class="border-t border-gray-200 max-h-80 overflow-y-auto"
              >
                <div
                  v-for="(item, index) in invalidItems"
                  :key="index"
                  class="p-3 border-b border-gray-100 last:border-b-0"
                >
                  <div class="flex items-start">
                    <span class="text-red-500 mr-2 mt-1">×</span>
                    <div>
                      <div class="font-medium text-sm">
                        <strong>Row {{ index + 2 }}:</strong> {{ getItemIdentifier(item) }}
                      </div>
                      <div class="text-red-600 text-sm mt-1">{{ item.failedReason }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Instructions Tab -->
        <div v-if="activeTab === 'instructions'" class="tab-content">
          <h3 class="text-lg font-semibold mb-4">File Requirements</h3>
          <ul class="space-y-2 mb-6">
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>File must be in Excel format (.xlsx, .xls)</span>
            </li>
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>First row must contain column headers exactly as specified</span>
            </li>
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>File should not contain empty rows</span>
            </li>
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>Maximum file size: 10MB</span>
            </li>
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>Maximum records per file: 1000</span>
            </li>
          </ul>

          <h3 class="text-lg font-semibold mb-4">Required Fields</h3>
          <div class="overflow-x-auto mb-6" v-if="requiredColumns.length > 0">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-100">
                  <th class="p-3 text-left border-b border-gray-200 text-gray-800">Field Name</th>
                  <th class="p-3 text-left border-b border-gray-200 text-gray-800">Type</th>
                  <th class="p-3 text-left border-b border-gray-200 text-gray-800">Required</th>
                  <th class="p-3 text-left border-b border-gray-200 text-gray-800">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(column, index) in requiredColumns" :key="index">
                  <td class="p-3 px-2 border-b border-gray-200">{{ column }}</td>
                  <td class="p-3 px-2 border-b border-gray-200">Text</td>
                  <td class="p-3 px-2 border-b border-gray-200">Yes</td>
                  <td class="p-3 px-2 border-b border-gray-200">Required field</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 class="text-lg font-semibold mb-4">Tips for Success</h3>
          <ul class="space-y-2">
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>Use text qualifiers (") if your data contains commas</span>
            </li>
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>Dates should be formatted as YYYY-MM-DD</span>
            </li>
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>Avoid special characters in field names</span>
            </li>
            <li class="flex items-start">
              <span class="text-blue-500 font-bold mr-3">•</span>
              <span>Save your file with UTF-8 encoding to preserve special characters</span>
            </li>
          </ul>
        </div>

        <!-- Template Tab -->
        <div v-if="activeTab === 'template'" class="tab-content">
          <div class="border border-gray-200 rounded-lg p-5 mb-5">
            <h3 class="text-lg font-semibold mb-3">Download Template</h3>
            <p class="text-gray-600 mb-4">
              Use this template to ensure your file has the correct format and required fields.
            </p>
            <v-btn
              @click="downloadSample"
              :disabled="downloadingSample"
              class="px-5 py-2 text-white rounded"
              v-if="sampleData && sampleData.length"
              color="primary"
            >
              <span v-if="downloadingSample">Downloading...</span>
              <span v-else>Download Template</span>
            </v-btn>
          </div>

          <h3 class="text-lg font-semibold mb-3">Template Preview</h3>
          <template>
            <div class="overflow-y-auto">
              <table class="table-css">
                <thead>
                  <tr>
                    <th v-for="(header, index) in templatePreview.headers" :key="index">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in templatePreview.rows" :key="rowIndex">
                    <td class="px-4 py-2" v-for="(cell, cellIndex) in row" :key="cellIndex">
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
          <p class="pt-2">
            <strong>Note:</strong> The first line must contain these exact column headers.
          </p>
        </div>

        <!-- Error Messages -->
        <div v-if="errorMessage" class="mt-5 p-4 border-l-4 border-red-500 bg-red-50 text-red-700">
          <div class="flex justify-between items-start">
            <span>{{ errorMessage }}</span>
            <button @click="errorMessage = null" class="text-red-500 ml-3">&times;</button>
          </div>
        </div>

        <!-- Success Messages -->
        <div
          v-if="successMessage"
          class="mt-5 p-4 border-l-4 border-green-500 bg-green-50 text-green-700"
        >
          <div class="flex justify-between items-start">
            <span>{{ successMessage }}</span>
            <button @click="successMessage = null" class="text-green-500 ml-3">&times;</button>
          </div>
        </div>
      </div>
      <div class="border-t px-5 py-3 bg-white" v-if="enableSave">
        <div class="flex justify-end gap-3">
          <v-btn text @click="cancel" class="cancel-btn"> Cancel </v-btn>
          <v-btn
            color="primary"
            @click="processFile"
            :loading="saving"
            :disabled="saving"
            class="save-btn"
            v-if="this.activeTab === 'upload'"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { extractExcelHeaders } from '@/utils/common'

export default {
  name: 'GenericBulkUpload',
  props: {
    title: {
      type: String,
      default: 'Bulk Upload'
    },
    subtitle: {
      type: String,
      default: 'Upload multiple items using Excel file'
    },
    entityName: {
      type: String,
      default: 'Item'
    },
    requiredColumns: {
      type: Array,
      default: () => []
    },
    uploadEndpoint: {
      type: String,
      required: true
    },
    successKey: {
      type: String,
      default: 'success'
    },
    failedKey: {
      type: String,
      default: 'failed'
    },
    invalidItemsKey: {
      type: String,
      default: 'invalidItems'
    },
    method: {
      type: String,
      default: 'post'
    },
    sampleData: {
      type: Array,
      default: () => []
    },
    additionalPayload: {
      type: Object,
      default: () => ({})
    },
    fieldMappings: {
      type: Object,
      default: () => ({})
    },
    enableSave: {
      type: Boolean,
      default: true
    },
    // Control parent save button visibility
    controlParentSaveButton: {
      type: Boolean,
      default: true
    },
    // Additional form data from parent component
    formData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // Tab management
      activeTab: 'upload',
      tabs: [
        { key: 'upload', label: 'Upload File' },
        { key: 'instructions', label: 'Instructions' },
        { key: 'template', label: 'Template' }
      ],

      // File handling
      selectedFile: null,
      isDragOver: false,
      uploading: false,
      validating: false,
      saving: false,
      uploadComplete: false,
      uploadProgress: 0,

      // Validation
      validationPassed: false,
      validationErrors: [],
      matchedHeaders: [],
      fileErrors: [],
      validationMessages: [],

      // Upload Results
      uploadResults: {
        success: 0,
        failed: 0
      },
      invalidItems: [],
      showFailedDetails: false,

      // Messages
      errorMessage: null,
      successMessage: null,

      // Download states
      downloadingFailed: false,
      downloadingSample: false
    }
  },
  computed: {
    templatePreview() {
      if (!this.sampleData || this.sampleData.length === 0) {
        return {
          headers: this.requiredColumns,
          rows: [
            ['Sample Data 1', 'Sample Data 2'],
            ['Sample Data 3', 'Sample Data 4']
          ]
        }
      }

      const headers = Object.keys(this.sampleData[0])
      const rows = this.sampleData
        .slice(0, 2)
        .map((row) => headers.map((header) => row[header] || ''))
      return { headers, rows }
    }
  },
  methods: {
    setActiveTab(tabKey) {
      this.activeTab = tabKey
      // this.$emit('tab-changed', tabKey)

      // // Control parent save button visibility if enabled
      if (this.controlParentSaveButton) {
        // Save button should be visible only on 'upload' tab
        // Hide on 'instructions' and 'template' tabs
        const shouldShowSaveButton = tabKey === 'upload'
        this.$emit('save-button-visibility', shouldShowSaveButton)
      }
    },
    cancel() {
      this.$emit('close')
    },
    handleDragOver(e) {
      e.preventDefault()
      e.stopPropagation()
    },

    handleDragEnter(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDragOver = true
    },

    handleDragLeave(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDragOver = false
    },

    handleDrop(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDragOver = false

      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.handleFileSelect({ target: { files: [files[0]] } })
      }
    },

    handleFileSelect(event) {
      this.clearMessages()
      this.validationMessages = []

      const file = event.target.files ? event.target.files[0] : event

      if (file) {
        // Validate file type
        const allowedTypes = [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
          'text/csv',
          'application/csv'
        ]

        const isValidType =
          allowedTypes.includes(file.type) ||
          file.name.endsWith('.csv') ||
          file.name.endsWith('.xlsx') ||
          file.name.endsWith('.xls')

        if (!isValidType) {
          this.validationMessages.push({
            type: 'error',
            text: 'Please select a valid Excel file (.xlsx, .xls)'
          })
          this.selectedFile = null
          return
        }

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          this.validationMessages.push({
            type: 'error',
            text: 'File size should be less than 10MB'
          })
          this.selectedFile = null
          return
        }

        this.selectedFile = file
        this.validationMessages.push({
          type: 'success',
          text: 'File format appears valid'
        })

        // Auto-validate file
        setTimeout(() => {
          this.validateFile()
        }, 500)
      }
    },

    removeFile() {
      this.selectedFile = null
      this.validationMessages = []
      this.resetValidation()
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    // Method to trigger upload from parent component
    processFile() {
      if (!this.selectedFile) return
      this.uploadFile()
    },

    async validateFile() {
      if (!this.selectedFile) return

      this.validating = true
      this.validationMessages = this.validationMessages.filter((msg) => msg.type !== 'error')

      try {
        // Extract headers from Excel/CSV file
        const headers = await extractExcelHeaders([this.selectedFile])

        // Check for missing required fields
        const missingFields = []
        const matchedFields = []

        this.requiredColumns.forEach((field) => {
          if (headers.includes(field)) {
            matchedFields.push(field)
          } else {
            missingFields.push(`Missing required column: ${field}`)
          }
        })

        this.matchedHeaders = matchedFields
        this.validationErrors = missingFields
        this.validationPassed = missingFields.length === 0

        // Update validation messages
        if (this.validationPassed) {
          this.validationMessages.push({
            type: 'success',
            text: 'Required columns found'
          })
          if (matchedFields.length > 0) {
            this.validationMessages.push({
              type: 'success',
              text: `Matched ${matchedFields.length} columns successfully`
            })
          }
        } else {
          missingFields.forEach((error) => {
            this.validationMessages.push({
              type: 'error',
              text: error
            })
          })
        }
      } catch (error) {
        console.error('Validation error:', error)
        this.validationMessages.push({
          type: 'error',
          text: 'Error processing the file. Please check the file format.'
        })
        this.validationPassed = false
      } finally {
        this.validating = false
      }
    },

    async uploadFile() {
      if (!this.selectedFile) return

      this.uploading = true
      this.saving = true
      this.uploadProgress = 0

      try {
        const formData = new FormData()
        formData.append('attachments', this.selectedFile)

        // Add additional payload from props
        Object.keys(this.additionalPayload).forEach((key) => {
          formData.append(key, this.additionalPayload[key])
        })

        // Add formData from props if provided
        // Object.keys(this.formData).forEach((key) => {
        //   formData.append(key, this.formData[key])
        // })

        const config = {
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
          }
        }

        const response = await this.$axios[this.method](this.uploadEndpoint, formData, config)

        // Process response - adjust based on your API response structure
        this.uploadResults = {
          success: response.data?.data[this.successKey] || 0,
          failed: response.data?.data[this.failedKey] || 0
        }

        this.invalidItems =
          response.data?.data[this.invalidItemsKey] || response.data?.invalidItems || []

        if (this.uploadResults.success > 0) {
          this.successMessage = `${this.uploadResults.success} ${this.entityName}s uploaded successfully!`
          this.$emit('uploadComplete', response.data)
        }

        this.uploadComplete = true
      } catch (error) {
        console.error('Upload error:', error)
        this.errorMessage =
          error.response?.data?.message || `Failed to upload ${this.entityName}s. Please try again.`
      } finally {
        this.uploading = false
        this.saving = false
      }
    },

    async downloadFailedItems() {
      if (this.invalidItems.length === 0) return

      this.downloadingFailed = true

      try {
        const XLSX = require('xlsx')

        // Prepare failed items data with reasons
        const failedData = this.invalidItems.map((item, index) => ({
          'Row Number': index + 2,
          'Failed Reason': item.failedReason,
          ...item
        }))

        // Create workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(failedData)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, `Failed ${this.entityName}s`)

        // Download file
        const fileName = `Failed_${this.entityName}s_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(workbook, fileName)
      } catch (error) {
        console.error('Download error:', error)
        this.errorMessage = `Failed to download failed ${this.entityName}s data.`
      } finally {
        this.downloadingFailed = false
      }
    },

    async downloadSample() {
      this.downloadingSample = true

      try {
        const XLSX = require('xlsx')

        const worksheet = XLSX.utils.json_to_sheet(this.sampleData)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, `${this.entityName} Sample`)

        XLSX.writeFile(workbook, `${this.entityName}_Upload_Sample.xlsx`)
      } catch (error) {
        console.error('Sample download error:', error)
        this.errorMessage = 'Failed to download sample file.'
      } finally {
        this.downloadingSample = false
      }
    },

    getItemIdentifier(item) {
      // Try to get a meaningful identifier for the item
      return (
        item['Application Number'] ||
        item['Customer Name'] ||
        item['EMI Amount'] ||
        item['Due Date'] ||
        item['CRN'] ||
        item['Case No'] ||
        `Unknown ${this.entityName}`
      )
    },

    resetValidation() {
      this.validationPassed = false
      this.validationErrors = []
      this.matchedHeaders = []
    },

    clearMessages() {
      this.errorMessage = null
      this.successMessage = null
    },

    closeDialog() {
      this.$emit('close')
    }
  },
  mounted() {
    if (this.controlParentSaveButton) {
      const shouldShowSaveButton = this.activeTab === 'upload'
      this.$emit('save-button-visibility', shouldShowSaveButton)
    }
  }
}
</script>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}

.tab-content {
  min-height: 300px;
  width: 100%;
}

.tab-items {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

/* Custom scrollbar for failed items list */
.max-h-80::-webkit-scrollbar {
  width: 6px;
}

.max-h-80::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.max-h-80::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.max-h-80::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Ensure proper spacing for dynamic content */
.space-y-2 > * + * {
  margin-top: 0.5rem;
}
.table-css,
table,
th,
td {
  padding: 10px;
  border: 1px solid #e5e7eb; /* Tailwind gray-200 */
  border-collapse: collapse;
}

/* Responsive table */
@media (max-width: 768px) {
  .overflow-x-auto table {
    font-size: 0.875rem;
  }

  .overflow-x-auto th,
  .overflow-x-auto td {
    padding: 0.5rem;
  }
}
</style>
